<template>
  <div class="edit-pet-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'EditUserDashboard_phone.png' : 'EditUserDashboard.png'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Edit Pet Page</h1>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="page-wrapper">
      <div class="form-card">
        <form @submit.prevent="updatePet">

          <div class="form-group">
            <label class="form-label">Name</label>
            <input v-model="form.pet_name" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Breed</label>
            <input v-model="form.breed" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Birthday</label>
            <input v-model="form.birthday" type="date" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Age Group</label>
            <div class="pill-group">
              <label v-for="option in ['Puppy', 'Adult', 'Senior']" :key="option"
                class="pill-option" :class="{ 'pill-active': form.age === option }">
                <input type="radio" v-model="form.age" :value="option" hidden required />
                {{ option }}
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Gender</label>
            <div class="pill-group">
              <label class="pill-option pill-male" :class="{ 'pill-active': form.gender === 'Male' }">
                <input type="radio" v-model="form.gender" value="Male" hidden />
                Male
              </label>
              <label class="pill-option pill-female" :class="{ 'pill-active': form.gender === 'Female' }">
                <input type="radio" v-model="form.gender" value="Female" hidden />
                Female
              </label>
            </div>
          </div>

          <div class="form-group checkbox-row">
            <input v-model="form.vaccinated" type="checkbox" class="checkbox-input" id="vaccinated" />
            <label class="checkbox-label" for="vaccinated">Vaccinated</label>
          </div>

          <div v-if="!form.vaccinated" class="vaccinated-warning">
            ⚠️ {{ form.pet_name || 'Your pet' }} won't appear to other users until {{ form.gender === 'Female' ? 'she' : 'he' }} is vaccinated.
          </div>

          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea v-model="form.description" class="form-textarea"></textarea>
          </div>

          <div class="form-group">
            <div class="days-label-row">
              <label class="form-label" style="margin:0;">Available Days</label>
              <div class="day-presets">
                <button type="button" class="preset-btn" @click="selectPreset('all')">All</button>
                <button type="button" class="preset-btn" @click="selectPreset('weekdays')">Weekdays</button>
                <button type="button" class="preset-btn" @click="selectPreset('weekends')">Weekends</button>
              </div>
            </div>

            <div class="days-group">
              <label
                v-for="day in weekdays"
                :key="day"
                class="day-pill"
                :class="{ 'day-active': form.available_days.includes(day) }"
              >
                <input type="checkbox" :value="day" v-model="form.available_days" hidden />
                {{ day.slice(0, 3) }}
              </label>
            </div>

            <button type="button" class="preset-btn preset-clear" style="margin-top: 10px;" @click="selectPreset('none')">Clear All</button>
          </div>

          <div class="form-group time-row">
            <div class="time-col">
              <label class="form-label">From</label>
              <input v-model="form.available_start_time" type="time" class="form-input" required />
            </div>
            <div class="time-sep">—</div>
            <div class="time-col">
              <label class="form-label">To</label>
              <input v-model="form.available_end_time" type="time" class="form-input" required />
            </div>
          </div>

          <button type="submit" class="submit-btn">Save Changes</button>

        </form>

        <div v-if="successMessage" class="feedback success">{{ successMessage }}</div>
        <div v-if="errorMessage" class="feedback error">{{ errorMessage }}</div>

      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'EditPetDashboard',
  props: {
    pet: {
      type: Object,
      default: () => ({})
    },
    user: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'change-page', 'pet-updated'],
  data() {
    return {
      weekdays: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
      form: {
        pet_name:             this.pet.pet_name,
        breed:                this.pet.breed,
        birthday:             this.pet.birthday ? this.pet.birthday.slice(0, 10) : '',
        vaccinated:           !!this.pet.vaccinated,
        age:                  this.pet.age,
        gender:               this.pet.gender,
        description:          this.pet.description,
        available_days:       this.pet.available_days
          ? (Array.isArray(this.pet.available_days)
            ? [...this.pet.available_days]
            : this.pet.available_days.split(',').map(day => day.trim()))
          : [],
        available_start_time: this.pet.available_start_time || '09:00',
        available_end_time:   this.pet.available_end_time   || '17:00'
      },
      successMessage: '',
      errorMessage: '',
      sidebarOpen: false,
      petsOpen: false,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
    };
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async updatePet() {
      if (!this.form.available_days.length) {
        this.errorMessage = 'Please select at least one available day.';
        return;
      }
      if (this.form.available_end_time <= this.form.available_start_time) {
        this.errorMessage = 'End time must be after start time.';
        return;
      }
      try {
        const response = await axios.patch(
          `${backendUrl}/api/pets/${this.pet.id}`,
          this.form,
          { withCredentials: true }
        );
        this.successMessage = response.data.message || 'Pet updated successfully';
        this.errorMessage = '';
        this.$emit('pet-updated', response.data.pet || this.form);
      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Failed to update pet';
        this.successMessage = '';
      }
    },
    selectPreset(type) {
      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      const weekends = ['Saturday', 'Sunday'];
      if (type === 'all')      this.form.available_days = [...this.weekdays];
      if (type === 'weekdays') this.form.available_days = [...weekdays];
      if (type === 'weekends') this.form.available_days = [...weekends];
      if (type === 'none')     this.form.available_days = [];
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
  },
  watch: {
    user: {
      handler(newVal) {
        console.log("User updated:", newVal);
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.edit-pet-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.page-wrapper {
  max-width: 760px;
  margin: 0 auto;
  padding: 14px 24px 20px;
}

.form-card {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f0e0ff;
  padding: 20px 26px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.form-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-textarea {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  color: #1a1a1a;
  transition: all 0.2s ease;
}

.form-textarea:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.vaccinated-warning {
  margin-top: -8px;
  margin-bottom: 14px;
  padding: 9px 13px;
  background: #fff7e6;
  border: 1.5px solid #f6c90e;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #92610a;
}

.days-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 6px;
}

.day-presets {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.pill-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.pill-option {
  flex: 1;
  text-align: center;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.pill-option:hover { border-color: #ba71ff; color: #4a1070; }

.pill-option.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-male.pill-active   { background: #eff6ff; border-color: #2563eb; color: #1d4ed8; }
.pill-female.pill-active { background: #fdf2f8; border-color: #db2777; color: #be185d; }

.days-group {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.day-pill {
  padding: 6px 10px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.day-pill:hover { border-color: #ba71ff; color: #4a1070; }

.day-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.preset-btn {
  padding: 5px 11px;
  border-radius: 999px;
  border: 1.5px solid #ba71ff;
  background: #f5eeff;
  color: #4a1070;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  font-family: inherit;
}

.preset-btn:hover { background: #eac7ff; border-color: #9b3ddd; }

.preset-clear {
  border-color: #e0e0e0;
  background: #f5f5f5;
  color: #999;
}

.preset-clear:hover {
  background: #ffe8e8;
  border-color: #e57373;
  color: #c0392b;
}

.time-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.time-col { flex: 1; }

.time-sep {
  font-size: 16px;
  color: #ccc;
  font-weight: 700;
  padding-bottom: 10px;
  flex-shrink: 0;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: #ba71ff;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
}

.submit-btn {
  width: 100%;
  padding: 11px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 4px;
  font-family: inherit;
}

.submit-btn:hover  { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }

.update-button {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 10px;
  background: #eac7ff;
  color: #4a1070;
  font-family: inherit;
}

.update-button:hover { background: #ba71ff; color: #fff; }
.update-button:active { transform: scale(0.98); }

.update-button[type="button"] {
  background: #fffbe8;
  color: #888;
  border: 1.5px solid #f0e0ff;
  box-shadow: none;
}

.update-button[type="button"]:hover {
  background: #f5f0d8;
  color: #555;
}

.feedback {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}

.success { background: #d4f5e2; color: #1a6b3c; }
.error   { background: #fde8e8; color: #991b1b; }

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

/* ── Desktop: 2 column grid ── */
@media (min-width: 600px) {
  form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 28px;
  }

  .form-group:has(.form-textarea),
  .form-group:has(.days-group),
  .form-group:has(.time-row),
  .checkbox-row,
  .vaccinated-warning,
  .submit-btn {
    grid-column: 1 / -1;
  }

  .form-input { padding: 11px 15px; font-size: 15px; border-radius: 13px; }
  .form-textarea { padding: 11px 15px; font-size: 15px; min-height: 80px; }
  .form-label { font-size: 12px; margin-bottom: 6px; }
  .form-group { margin-bottom: 16px; }
  .pill-option { padding: 9px 16px; font-size: 13px; }
  .day-pill { padding: 7px 12px; font-size: 12px; }
  .preset-btn { padding: 6px 13px; font-size: 12px; }
  .submit-btn { padding: 13px; font-size: 15px; }
  .checkbox-label { font-size: 14px; }
  .update-button { padding: 13px; font-size: 15px; }
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .page-wrapper { padding: 12px 12px 16px; }

  /* form specifically smaller, not the whole page */
  .form-card { padding: 10px 12px; }
  .form-group { margin-bottom: 8px; }
  .form-label { font-size: 9px; margin-bottom: 3px; }
  .form-input { padding: 6px 9px; font-size: 11px; border-radius: 8px; }
  .form-textarea { padding: 6px 9px; font-size: 11px; min-height: 45px; border-radius: 8px; }
  .pill-option { padding: 5px 9px; font-size: 10px; }
  .day-pill { padding: 4px 7px; font-size: 10px; }
  .preset-btn { padding: 3px 7px; font-size: 9px; }
  .checkbox-label { font-size: 11px; }
  .vaccinated-warning { font-size: 10px; padding: 6px 9px; }
  .submit-btn { padding: 8px; font-size: 11px; }
  .time-row { flex-direction: column; gap: 0; }
  .time-sep { display: none; }
  .time-col { width: 100%; }
  .update-button { padding: 8px; font-size: 11px; }
}
</style>